import React, { useMemo } from "react";
import {
    Box,
    Typography,
    Chip,
} from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import FlexBetween from "components/FlexBetween";

const safeNumber = (v) => (typeof v === "number" ? v : 0);

const FIELDS_LIST = [
    { key: "totalHoursAvg", label: "Total Hours Average" },
    { key: "totalHoursDeviation", label: "Total Hours Deviation" },
    { key: "blockHours", label: "Block Hours" },
    { key: "sectors", label: "Sectors" },
    { key: "lineTrainingBlockHours", label: "Instructor Hours" },
    { key: "lineTrainingSectors", label: "Instructor Sectors" },
    { key: "instructorSimHours", label: "Sim Hours" },
    { key: "airHours", label: "Air Hours" },
    { key: "dhdHours", label: "Pax Hours" },
    { key: "dhdSectors", label: "Pax Sectors" },
    { key: "observeBlockHours", label: "Observer Hours" },
    { key: "observeSectors", label: "Observer Sectors" },
    { key: "flyingDays", label: "Flying Days" },
    { key: "nonFlyingDays", label: "Non-Flying Days" },
    { key: "flyingDaysAvg", label: "Flying Days Average" },
    { key: "flyingDaysDeviation", label: "Flying Days Deviation" },
    { key: "nonFlyingDaysAvg", label: "Non-Flying Days Average" },
    { key: "nonFlyingDaysDeviation", label: "Non-Flying Days Deviation" },
    { key: "standbyCount", label: "Standby" },
    { key: "noshowCount", label: "No Show" },
    { key: "sickCount", label: "Sick" },
    { key: "sickOnHolidayAndWeekendCount", label: "Sick on Weekend" },
    { key: "fixedOffCount", label: "Day-Off" },
    { key: "fixedOffOnHolidayAndWeekendCount", label: "Day-Off on Weekend" },
];

const MetricTree = React.memo(({ data }) => {   

    const crews = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [];

        return Object.values(data)?.map((c) => ({
            crewCode: c.crewCode,
            crewName: c.crewName,
            post: c.post,
            base: c.base,

            totalHours: safeNumber(c.totalHours),

            totalHoursAvg: safeNumber(c.totalHoursAvg),
            totalHoursDeviation: safeNumber(c.totalHoursDeviation),
            blockHours: safeNumber(c.blockHours),
            sectors: safeNumber(c.sectors),
            lineTrainingBlockHours: safeNumber(c.lineTrainingBlockHours),
            lineTrainingSectors: safeNumber(c.lineTrainingSectors),
            instructorSimHours: safeNumber(c.instructorSimHours),
            airHours: safeNumber(c.airHours),
            dhdHours: safeNumber(c.dhdHours),
            dhdSectors: safeNumber(c.dhdSectors),
            observeBlockHours: safeNumber(c.observeBlockHours),
            observeSectors: safeNumber(c.observeSectors),
            flyingDays: safeNumber(c.flyingDays),
            nonFlyingDays: safeNumber(c.nonFlyingDays),
            flyingDaysAvg: safeNumber(c.flyingDaysAvg),
            flyingDaysDeviation: safeNumber(c.flyingDaysDeviation),
            nonFlyingDaysAvg: safeNumber(c.nonFlyingDaysAvg),
            nonFlyingDaysDeviation: safeNumber(c.nonFlyingDaysDeviation),
            standbyCount: safeNumber(c.standbyCount),
            noshowCount: safeNumber(c.noshowCount),
            sickCount: safeNumber(c.sickCount),
            sickOnHolidayAndWeekendCount: safeNumber(c.sickOnHolidayAndWeekendCount),
            fixedOffCount: safeNumber(c.fixedOffCount),
            fixedOffOnHolidayAndWeekendCount: safeNumber(c.fixedOffOnHolidayAndWeekendCount),
        }))
        .sort((a, b) => b.totalHours - a.totalHours);
    }, [data]);

    
    return (
        <Box sx={{ pr: 1 }}>      
            <FlexBetween
                sx={{
                    position: 'sticky',
                    top: 0,
                    // zIndex: (theme) => theme.zIndex.appBar - 99,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderTop: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 2,
                    py: 1,
                }}
                >
                <Typography
                    variant="subtitle1"
                    color="text.primary"
                >
                    Crew List
                </Typography>

                <Typography
                    variant="subtitle1"
                    color="text.primary"
                >
                    Total Hours
                </Typography>
                </FlexBetween>


            <SimpleTreeView
                defaultExpandedItems={[]}
                slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
            >
                {crews.map((crew) => {
                    const {
                        crewCode,
                        crewName,
                        post,
                        base,
                        totalHours,
                    } = crew;

                    return (
                        <TreeItem
                            key={crewCode}
                            itemId={crewCode}
                            label={
                                <FlexBetween>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <Typography variant="body2">
                                            {crewCode} - {crewName}
                                        </Typography>

                                        <Chip
                                            label={`${post} | ${base}`}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '0.65rem',                                           }}
                                        />
                                    </Box>

                                    <Typography variant="body2">
                                        {safeNumber(totalHours).toFixed(1)}
                                    </Typography>
                                </FlexBetween>
                            }
                        >
                            <Box 
                                display="grid" 
                                gridTemplateColumns="1fr 1fr" 
                                px={2} py={1} 
                                gap={1}
                            >
                                {FIELDS_LIST.map(({ key, label }) => (
                                    <FlexBetween key={key}>
                                        <Typography variant="body2" color="text.secondary">
                                            {label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {safeNumber(crew[key]).toFixed(1)}
                                        </Typography>
                                    </FlexBetween>
                                ))}
                            </Box>
                        </TreeItem>
                    );
                })}
            </SimpleTreeView>
        </Box>
    );
});

export default MetricTree;
